import React, { useState } from 'react';
import { useLocation, Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GridViewIcon from '@mui/icons-material/GridView'; 
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import Logout from '@mui/icons-material/Logout';

const drawerWidth = 280;

const NAVIGATION_ITEMS = [
  // { text: 'Dashboard', icon: <DashboardOutlinedIcon />, path: '/admin' },
  { text: 'Dispute Manager', icon: <ErrorOutlineIcon />, path: '/admin' },
  { text: 'Teacher Approval', icon: <PersonOutlineIcon />, path: '/admin/pending-teachers' }, 
  { text: 'Transactions', icon: <ReceiptOutlinedIcon />, path: '/admin/transactions' },
  { text: 'Configuration', icon: <SettingsOutlinedIcon />, path: '/admin/configuration' },
  { text: 'Newsfeed', icon: <FeedOutlinedIcon />, path: '/admin/newsfeed' },
];

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async() => {
    await logout();
    navigate("/");
  };

  const drawer = (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 3, gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: '12px',
            width: 48,
            height: 48,
          }}
        >
          <GridViewIcon />
        </Box>
        <Box>
          <Typography variant="h2" sx={{ m: 0, fontSize: '18px', color: 'text.primary' }}>
            Admin Panel
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Ethio Future Tutor
          </Typography>
        </Box>
      </Box>

      <List sx={{ px: 2 }}>
        {NAVIGATION_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;

          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                onClick={() => setMobileOpen(false)} 
                sx={{
                  borderRadius: 1,
                  bgcolor: isActive ? 'rgba(0, 128, 129, 0.08)' : 'transparent',
                  color: isActive ? 'primary.main' : 'text.primary',
                  '&:hover': {
                    bgcolor: 'rgba(0, 128, 129, 0.12)', 
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: isActive ? 'primary.main' : 'text.secondary' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    variant: 'body1',
                    fontWeight: isActive ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            component={Link}
            onClick={() => handleLogout()} 
            sx={{
              borderRadius: 1,
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'rgba(0, 128, 129, 0.12)', 
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
              <Logout />
            </ListItemIcon>
            <ListItemText
              primary="Log out"
              primaryTypographyProps={{
                variant: 'body1',
                fontWeight: 600,
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'block', sm: 'none' },
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
              Admin Panel
            </Typography>
          </Box>
          <IconButton color="inherit" edge="end">
            <AccountCircleIcon fontSize="large" />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="admin navigation folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, 
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none' },
          }}
        >
          {drawer}
        </Drawer>
        
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #f0f0f0' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* FIXED CONTENT AREA */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          /* Remove default padding on mobile to prevent horizontal pushing */
          p: { xs: 0, sm: 3 }, 
          /* Force standard box sizing */
          boxSizing: 'border-box',
          /* Calculate exact width required on mobile vs desktop */
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          /* Prevent any horizontal overflow */
          maxWidth: '100vw',
          overflowX: 'hidden',
          minHeight: '100vh',
          bgcolor: '#fafafa', 
        }}
      >
        <Toolbar sx={{ display: { sm: 'none' } }} />
        <Outlet /> 
      </Box>
    </Box>
  );
}